import React from 'react';
import { PageType } from '../../../Model/PageData';

type EmulatorSenderAction = {
    type: string;
    payload: unknown;
};

type EmulatorWindow = Window & {
    SENDER: (action: EmulatorSenderAction) => void;
    RECEIVER?: (action: { type: string; payload: { page: PageType } }) => void;
    MESSAGE?: string;
};

const RECEIVER_ACTION_CHANGE_PAGE = 'CHANGE_PAGE';

export const emulatorVendStateOptions = [
    {
        label: 'Vend success',
        value: PageType.SCREEN_VEND_SUCCESS,
    },
    {
        label: 'Vend error',
        value: PageType.SCREEN_VEND_ERROR,
    },
    {
        label: 'Product out of stock',
        value: PageType.SCREEN_PRODUCT_OUT_OF_STOCK,
    },
    {
        label: 'Duplicate QR',
        value: PageType.SCREEN_DUPLICATE_QR,
    },
    {
        label: 'Invalid QR',
        value: PageType.SCREEN_INVALID_QR,
    },
    {
        label: 'Default screen',
        value: PageType.SCREEN_DEFAULT,
    },
] as const;

const changePage = (page: PageType) => {
    (window as EmulatorWindow).RECEIVER?.({
        type: RECEIVER_ACTION_CHANGE_PAGE,
        payload: { page },
    });
};

const sendQrScan = (value: string) => {
    document.dispatchEvent(
        new CustomEvent<{ value: string }>('QR_SCAN_READY', {
            bubbles: false,
            detail: { value },
        }),
    );
};

function useEmulator(active: boolean) {
    const [configOpen, setConfigOpen] = React.useState(false);
    const [qrValue, setQrValue] = React.useState('');
    const [vendState, setVendState] = React.useState<PageType>(
        PageType.SCREEN_VEND_SUCCESS,
    );

    const simulatedSender = React.useCallback(
        (action: EmulatorSenderAction) => {
            switch (action.type) {
                case 'POST_LOG':
                    console.log('[Builder emulator] POST_LOG', action.payload);
                    break;
                case 'PRODUCT_VEND':
                case 'VEND':
                case 'VEND_BY_TOKEN':
                    changePage(PageType.SCREEN_VENDING);
                    window.setTimeout(() => {
                        changePage(vendState);
                    }, 400);
                    break;
                default:
                    break;
            }
        },
        [vendState],
    );

    React.useEffect(() => {
        if (!active) {
            return undefined;
        }

        const runtimeWindow = window as EmulatorWindow;
        const timeoutId = window.setTimeout(() => {
            runtimeWindow.SENDER = simulatedSender;
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
            if (runtimeWindow.SENDER === simulatedSender) {
                runtimeWindow.SENDER = () => undefined;
            }
        };
    }, [active, simulatedSender]);

    const handleSendQr = React.useCallback(() => {
        sendQrScan(qrValue);
    }, [qrValue]);

    const handleReset = React.useCallback(() => {
        setQrValue('');
        (window as EmulatorWindow).MESSAGE = '';
        setConfigOpen(false);
    }, []);

    return {
        configOpen,
        openConfig: () => setConfigOpen(true),
        closeConfig: handleReset,
        qrValue,
        setQrValue,
        sendQr: handleSendQr,
        vendState,
        setVendState,
    };
}

export default useEmulator;
